import { useEffect, useMemo, useState, useCallback, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Navigation, Layers, Route as RouteIcon, CloudRain, Sun, Accessibility, CalendarPlus, AlertTriangle, WifiOff, Footprints, Bus, Bike, Zap, Phone, ShieldCheck, Trophy, HeartPulse, Store, Crosshair, Building2, CalendarCheck, Users, Search } from "lucide-react";
import { toast } from "sonner";
import { api, API } from "@/lib/api";
import { cacheGet, cacheSet } from "@/lib/offline";
import { useAuth } from "@/context/AuthContext";
import { useMode } from "@/lib/appearance";
import AppShell from "@/components/AppShell";
import CampusMap from "@/components/CampusMap";
import ScheduleBuilder from "@/components/ScheduleBuilder";
import GapCard, { Leg } from "@/components/GapCard";
import ReminderBanner from "@/components/ReminderBanner";
import IosInstallHint from "@/components/IosInstallHint";
import WeekSummary from "@/components/WeekSummary";
import RewardTracker from "@/components/RewardTracker";
import WelcomeChecklist from "@/components/WelcomeChecklist";
import TermAlert from "@/components/TermAlert";
import CrewStrip from "@/components/CrewStrip";
import NearYouRow from "@/components/NearYouRow";
import CampusCompanion from "@/components/CampusCompanion";
import ClassCheckinButton from "@/components/ClassCheckinButton";
import DirectionsModal from "@/components/DirectionsModal";
import OriginPicker from "@/components/OriginPicker";
import AppointmentsStrip from "@/components/AppointmentsStrip";

const DAYS = ["Mon", "Tue", "Wed", "Thu", "Fri"];
const todayName = () => {
  const d = new Date().getDay();
  return ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"][d];
};
const FALLBACK_CENTER = [30.6146, -96.3417];

export default function Dashboard() {
  const { user } = useAuth();
  const [campus, setCampus] = useState(() => cacheGet("campus"));
  const [classes, setClasses] = useState(() => cacheGet("classes") || []);
  const [plan, setPlan] = useState(null);
  const [day, setDay] = useState(DAYS.includes(todayName()) ? todayName() : "Mon");
  const [userLoc, setUserLoc] = useState(null);
  const [origin, setOrigin] = useState(null);
  const [focus, setFocus] = useState(null);
  const [highlight, setHighlight] = useState(null);
  const [filters, setFilters] = useState({ buildings: true, amenities: true, bus: true });
  const [showPhones, setShowPhones] = useState(false);
  const [showAccess, setShowAccess] = useState(false);
  const [showAthletics, setShowAthletics] = useState(false);
  const [showHealth, setShowHealth] = useState(false);
  const [showServices, setShowServices] = useState(false);
  const [showDorms, setShowDorms] = useState(false);
  const [showGreek, setShowGreek] = useState(false);
  const [mode, setMode] = useState(() =>
    new URLSearchParams(window.location.search).get("view") === "full" ? "full" : "mine");
  const _location = useLocation();
  const _navigate = useNavigate();
  const dark = useMode() === "dark";
  useEffect(() => {
    if (new URLSearchParams(_location.search).get("view") === "full") setMode("full");
  }, [_location.search]);
  const [collegeFilter, setCollegeFilter] = useState(() => localStorage.getItem("routerev_college") || "");
  const [weather, setWeather] = useState(null);
  const [rain, setRain] = useState(false);
  const [accessible, setAccessible] = useState(false);
  const [dirIndex, setDirIndex] = useState(null);
  const [offline, setOffline] = useState(false);
  const [semester, setSemester] = useState(null);
  const [directionsOn, setDirectionsOn] = useState(() => {
    const v = localStorage.getItem("routerev_directions");
    return v === null ? true : v === "true";
  });
  const [pref, setPref] = useState(user?.travel_pref || "fastest");
  const [activeCheckin, setActiveCheckin] = useState(null);
  const [layersOpen, setLayersOpen] = useState(false);

  // Demo campus: default the start point to an on-campus dorm so routes are realistic
  // (otherwise it falls back to the presenter's real GPS, thousands of miles away).
  const demoOriginSet = useRef(false);
  useEffect(() => {
    if (!demoOriginSet.current && campus?.school?.id === "demo" && !origin) {
      demoOriginSet.current = true;
      setOrigin({ id: "demo_start", name: "Nebula Hall", lat: 39.7716, lng: -86.1555 });
    }
  }, [campus, origin]);

  // Off-campus students: default Today's start point to their saved Home (skip demo campus).
  const homeApplied = useRef(false);
  useEffect(() => {
    if (!homeApplied.current && user?.home_origin && !origin && campus?.school?.id && campus.school.id !== "demo") {
      homeApplied.current = true;
      const h = user.home_origin;
      setOrigin({ id: "home", name: h.label, lat: h.lat, lng: h.lng });
    }
  }, [campus, origin, user]);

  const changePref = async (p) => {
    setPref(p);
    try { await api.put("/me/travel-pref", { pref: p }); } catch {}
  };

  const CENTER = campus?.school?.center || FALLBACK_CENTER;
  const effectiveLoc = origin ? [origin.lat, origin.lng] : (userLoc || CENTER);
  const activeLayerCount = [showPhones, showAccess, showAthletics, showHealth, showServices, showDorms, showGreek].filter(Boolean).length;

  useEffect(() => {
    api.get("/semester").then((r) => setSemester(r.data)).catch(() => {});
    api.get("/checkins/active").then((r) => setActiveCheckin(r.data.active)).catch(() => {});
  }, []);

  const myBuildingIds = useMemo(
    () => (plan ? plan.classes.map((c) => c.building_id).filter(Boolean) : []),
    [plan]
  );
  const myAmenityIds = useMemo(
    () => (plan ? Array.from(new Set(plan.gaps.flatMap((g) => g.recommendations.map((r) => r.id)))) : []),
    [plan]
  );

  useEffect(() => {
    api.get("/campus").then((r) => { setCampus(r.data); cacheSet("campus", r.data); }).catch(() => setOffline(true));
    navigator.geolocation?.getCurrentPosition(
      (pos) => setUserLoc([pos.coords.latitude, pos.coords.longitude]),
      () => {},
      { timeout: 5000 }
    );
  }, []);

  // Weather follows the student's location, chosen origin, or — as a fallback —
  // the selected school's own city center, so it stays correct as we add campuses.
  useEffect(() => {
    const [lat, lng] = effectiveLoc || [];
    if (lat == null) return;
    api.get(`/weather?lat=${lat}&lng=${lng}`)
      .then((r) => { setWeather(r.data); setRain(!!r.data.raining); })
      .catch(() => {});
  }, [effectiveLoc?.[0], effectiveLoc?.[1]]);

  const loadClasses = useCallback(() => {
    api.get("/classes").then((r) => { setClasses(r.data); cacheSet("classes", r.data); }).catch(() => setOffline(true));
  }, []);
  useEffect(() => { loadClasses(); }, [loadClasses]);

  useEffect(() => {
    const loc = effectiveLoc;
    api.get(`/plan?day=${day}&lat=${loc[0]}&lng=${loc[1]}&rain=${rain}&accessible=${accessible}&pref=${pref}`)
      .then((r) => { setPlan(r.data); setOffline(false); })
      .catch(() => setOffline(true));
  }, [day, userLoc, origin, classes, rain, accessible, campus, pref]);

  const exportCalendar = async () => {
    try {
      const res = await api.post("/share");
      const url = `${API}/calendar/${res.data.share_id}.ics`;
      window.open(url, "_blank");
      toast.success("Calendar (.ics) downloading — import into Google/Apple Calendar");
    } catch {
      toast.error("Could not export calendar");
    }
  };

  const legEndpoints = useMemo(() => {
    if (!plan || !campus) return [];
    const start = effectiveLoc;
    return plan.classes.map((c, i) => {
      const b = campus.buildings.find((x) => x.id === c.building_id);
      const to = (c.lat != null && c.lng != null) ? [c.lat, c.lng] : (b ? [b.lat, b.lng] : CENTER);
      const prev = i === 0 ? start : (() => {
        const pc = plan.classes[i - 1];
        if (pc.lat != null && pc.lng != null) return [pc.lat, pc.lng];
        const pb = campus.buildings.find((x) => x.id === pc.building_id);
        return pb ? [pb.lat, pb.lng] : CENTER;
      })();
      return { from: prev, to };
    });
  }, [plan, campus, userLoc, origin]);

  const legPolylines = useMemo(() => {
    if (!plan || !campus) return [];
    return plan.legs
      .map((leg, i) => {
        const geo = leg.travel?.recommended === "bike" && leg.travel?.bike_geometry?.length > 1
          ? leg.travel.bike_geometry
          : leg.travel?.geometry;
        if (geo?.length > 1) return geo;
        const e = legEndpoints[i];
        return e ? [e.from, e.to] : null;
      });
  }, [plan, campus, legEndpoints]);

  return (
    <AppShell>
      <div className="px-4 lg:px-6 py-5">
        {/* Header */}
        <div className="mb-4 pr-16 lg:pr-20">
          <div className="mono text-[11px] tracking-[0.2em] uppercase text-brand flex items-center gap-2">
            {campus?.school?.city || "College Station"}{campus?.school?.state ? `, ${campus.school.state}` : ""}
            {offline && (
              <span className="flex items-center gap-1 text-muted-foreground normal-case tracking-normal">
                <WifiOff size={11} strokeWidth={1.5} /> offline — showing saved data
              </span>
            )}
          </div>
          <h1 className="font-display text-3xl md:text-4xl font-semibold tracking-tight">
            {user?.theme?.greeting || "Howdy"}, {user?.name?.split(" ")[0] || "there"}!
          </h1>
        </div>

        <button
          data-testid="today-search-pill"
          onClick={() => _navigate("/places")}
          className="w-full mb-5 flex items-center gap-3 rounded-full bg-secondary border border-border px-4 py-3 text-left text-muted-foreground hover:border-brand transition-colors"
        >
          <Search size={17} strokeWidth={1.8} className="shrink-0" />
          <span className="text-sm">Find events, people, or places</span>
        </button>

        <CampusCompanion />

        <TermAlert onChanged={loadClasses} />
        <WelcomeChecklist />
        <CrewStrip />

        <RewardTracker />

        <AppointmentsStrip classes={classes} buildings={campus?.buildings || []} onPickDay={setDay} userLoc={effectiveLoc} pref={pref} />

        <div className="flex gap-1.5 overflow-x-auto hide-scrollbar mb-4" data-testid="day-selector">
          {DAYS.map((d) => (
            <button
              key={d}
              data-testid={`day-tab-${d}`}
              onClick={() => setDay(d)}
              className={`px-4 py-2 rounded-md text-sm font-bold mono transition-colors shrink-0 ${
                day === d ? "bg-brand text-white" : "bg-white border border-border hover:border-brand"
              }`}
            >
              {d}
            </button>
          ))}
        </div>

        {/* Controls row */}
        <div className="flex flex-wrap items-center gap-2 mb-5">
          <OriginPicker center={CENTER} value={origin} onChange={setOrigin} />
          {weather && weather.temp_f != null && (
            <span data-testid="weather-chip" className="flex items-center gap-1.5 bg-white border border-border rounded-md px-3 py-2 text-sm font-semibold">
              {weather.raining ? <CloudRain size={15} strokeWidth={1.5} className="text-blue-600" /> : <Sun size={15} strokeWidth={1.5} className="text-[#F59E0B]" />}
              {weather.temp_f}°F {weather.raining ? "· Rainy" : "· Clear"}
            </span>
          )}
          <button
            data-testid="rain-toggle"
            onClick={() => setRain((r) => !r)}
            className={`flex items-center gap-1.5 rounded-md px-3 py-2 text-sm font-semibold transition-colors ${rain ? "bg-blue-600 text-white" : "bg-white border border-border hover:border-brand"}`}
          >
            <CloudRain size={15} strokeWidth={1.5} /> Rain-aware
          </button>
          <button
            data-testid="accessible-toggle"
            onClick={() => setAccessible((a) => !a)}
            className={`flex items-center gap-1.5 rounded-md px-3 py-2 text-sm font-semibold transition-colors ${accessible ? "bg-[#16A34A] text-white" : "bg-white border border-border hover:border-brand"}`}
          >
            <Accessibility size={15} strokeWidth={1.5} /> Step-free routes
          </button>
          <button
            data-testid="directions-toggle"
            onClick={() => setDirectionsOn((d) => { localStorage.setItem("routerev_directions", String(!d)); return !d; })}
            title={directionsOn ? "Turn-by-turn directions on — tap a route for steps" : "Directions off — I know my way around"}
            className={`flex items-center gap-1.5 rounded-md px-3 py-2 text-sm font-semibold transition-colors ${directionsOn ? "bg-brand text-white" : "bg-white border border-border hover:border-brand"}`}
          >
            <RouteIcon size={15} strokeWidth={1.5} /> Directions {directionsOn ? "on" : "off"}
          </button>
          <div className="flex items-center rounded-md bg-white border border-border overflow-hidden" data-testid="travel-pref-switch" title="Your default way to get to class">
            {[
              { k: "fastest", label: "Auto", icon: Zap },
              { k: "walk", label: "Walk", icon: Footprints },
              { k: "bike", label: "Bike", icon: Bike },
              { k: "bus", label: "Bus", icon: Bus },
            ].map((p) => (
              <button
                key={p.k}
                data-testid={`travel-pref-${p.k}`}
                onClick={() => changePref(p.k)}
                className={`flex items-center gap-1.5 px-3 py-2 text-sm font-semibold transition-colors ${pref === p.k ? "bg-brand text-white" : "text-muted-foreground hover:bg-secondary"}`}
              >
                <p.icon size={15} strokeWidth={1.5} /> {p.label}
              </button>
            ))}
          </div>
          <button
            data-testid="calendar-export-btn"
            onClick={exportCalendar}
            className="flex items-center gap-1.5 rounded-md px-3 py-2 text-sm font-semibold bg-white border border-border hover:border-brand transition-colors ml-auto"
          >
            <CalendarPlus size={15} strokeWidth={1.5} /> Export to calendar
          </button>
        </div>

        {plan?.alerts?.length > 0 && (
          <div className="mb-5 space-y-2" data-testid="conflict-alerts">
            {plan.alerts.map((a, i) => (
              <div
                key={i}
                data-testid={`alert-${a.type}`}
                className={`flex items-center gap-3 rounded-xl px-4 py-3 border ${
                  a.type === "conflict" || a.type === "impossible"
                    ? "bg-destructive/5 border-destructive/30 text-destructive"
                    : "bg-[#F59E0B]/10 border-[#F59E0B]/40 text-[#92400e]"
                }`}
              >
                <AlertTriangle size={18} strokeWidth={1.5} className="shrink-0" />
                <span className="text-sm font-medium">{a.message}</span>
              </div>
            ))}
          </div>
        )}

        <WeekSummary classes={classes} onPickDay={setDay} userLoc={effectiveLoc} today={todayName()} rain={rain} accessible={accessible} pref={pref} />

        <IosInstallHint />

        <ReminderBanner classesVersion={classes.length} userLoc={userLoc} rain={rain} accessible={accessible} semester={semester} pref={pref} />

        <div className="grid grid-cols-12 gap-4 lg:gap-6">
          {/* Map */}
          <div className="col-span-12 lg:col-span-8 order-1">
            <div className="relative h-[380px] lg:h-[520px] rounded-xl overflow-hidden border border-border shadow-sm">
              {campus && (
                <CampusMap
                  campus={campus}
                  userLoc={effectiveLoc}
                  legs={legPolylines}
                  filters={filters}
                  focus={focus}
                  mode={mode}
                  myBuildingIds={myBuildingIds}
                  myAmenityIds={myAmenityIds}
                  showPhones={showPhones}
                  showAccess={showAccess}
                  showAthletics={showAthletics}
                  showHealth={showHealth}
                  showServices={showServices}
                  showDorms={showDorms}
                  showGreek={showGreek}
                  onSetOrigin={setOrigin}
                  highlightIdx={highlight}
                  highlightBuildingId={highlight != null ? (plan?.legs[highlight]?.to_building_id || null) : null}
                  collegeFilter={mode === "full" ? collegeFilter : ""}
                  dark={dark}
                />
              )}
              {/* View mode switch */}
              <div className="absolute top-3 left-3 z-[500] backdrop-blur-xl bg-white/85 border border-white/50 rounded-lg p-1 shadow-sm flex gap-1" data-testid="view-mode-switch">
                {[
                  { k: "mine", label: "My day" },
                  { k: "full", label: "Full campus" },
                ].map((v) => (
                  <button
                    key={v.k}
                    data-testid={`view-${v.k}`}
                    onClick={() => setMode(v.k)}
                    className={`text-xs font-bold px-3 py-1.5 rounded transition-colors ${
                      mode === v.k ? "bg-brand text-white" : "text-muted-foreground hover:bg-secondary"
                    }`}
                  >
                    {v.label}
                  </button>
                ))}
              </div>
              {/* Consolidated map layers popover */}
              <div className="absolute top-14 left-3 z-[500]" data-testid="map-layers-control">
                <button
                  data-testid="map-layers-toggle"
                  onClick={() => setLayersOpen((v) => !v)}
                  title="Toggle map layers — safety, accessible, athletics, health & services"
                  className={`flex items-center gap-1.5 text-xs font-bold px-2.5 py-1.5 rounded-lg shadow-sm border transition-colors ${layersOpen ? "bg-brand text-white border-brand" : "bg-white/85 backdrop-blur-xl border-white/50 text-foreground hover:bg-white"}`}
                >
                  <Layers size={13} strokeWidth={1.8} /> Layers
                  {activeLayerCount > 0 && (
                    <span className={`inline-flex items-center justify-center min-w-[16px] h-4 px-1 rounded-full text-[11px] font-bold ${layersOpen ? "bg-white text-brand" : "bg-brand text-white"}`}>{activeLayerCount}</span>
                  )}
                </button>
                {layersOpen && (
                  <div className="mt-1 w-56 backdrop-blur-xl bg-white/90 border border-white/60 rounded-lg p-2 shadow-lg" data-testid="map-layers-panel">
                    <div className="mono text-[11px] tracking-widest uppercase text-muted-foreground px-1 pb-1">Places on map</div>
                    <div className="space-y-0.5">
                      {[
                        { label: "Safety phones", icon: Phone, on: showPhones, set: setShowPhones, testid: "toggle-phones", color: "#1d4ed8" },
                        { label: "Accessible entrances", icon: Accessibility, on: showAccess, set: setShowAccess, testid: "toggle-access", color: "#0369a1" },
                        { label: "Athletics venues", icon: Trophy, on: showAthletics, set: setShowAthletics, testid: "toggle-athletics", color: "#7c3aed" },
                        { label: "Health & medical", icon: HeartPulse, on: showHealth, set: setShowHealth, testid: "toggle-health", color: "#0891b2" },
                        { label: "Shops & services", icon: Store, on: showServices, set: setShowServices, testid: "toggle-services", color: "#ea580c" },
                        { label: "On-campus dorms", icon: Building2, on: showDorms, set: setShowDorms, testid: "toggle-dorms", color: "#15803d" },
                        { label: "Greek houses", icon: Users, on: showGreek, set: setShowGreek, testid: "toggle-greek", color: "#b45309" },
                      ].map((l) => (
                        <button
                          key={l.testid}
                          data-testid={l.testid}
                          onClick={() => l.set((v) => !v)}
                          className={`w-full flex items-center gap-2 text-left text-xs font-semibold px-2 py-1.5 rounded-md transition-colors ${l.on ? "text-white" : "text-foreground hover:bg-secondary"}`}
                          style={l.on ? { backgroundColor: l.color } : undefined}
                        >
                          <l.icon size={14} strokeWidth={1.8} /> {l.label}
                        </button>
                      ))}
                    </div>
                    {mode === "full" && (
                      <div className="mt-1.5 pt-1.5 border-t border-border/60">
                        <div className="mono text-[11px] tracking-widest uppercase text-muted-foreground px-1 pb-1">Campus overlays</div>
                        <div className="space-y-0.5">
                          {[
                            { k: "buildings", label: "Buildings" },
                            { k: "amenities", label: "Dining / Study / Gym" },
                            { k: "bus", label: "Bus routes" },
                          ].map((f) => (
                            <button
                              key={f.k}
                              data-testid={`layer-${f.k}`}
                              onClick={() => setFilters((s) => ({ ...s, [f.k]: !s[f.k] }))}
                              className={`w-full text-left text-xs font-semibold px-2 py-1.5 rounded-md transition-colors ${filters[f.k] ? "bg-brand text-white" : "text-foreground hover:bg-secondary"}`}
                            >
                              {f.label}
                            </button>
                          ))}
                        </div>
                        {campus?.colleges?.length > 0 && (
                          <div className="mt-1.5 pt-1.5 border-t border-border/60">
                            <div className="flex items-center gap-1.5 px-1 pb-1 mono text-[11px] tracking-widest uppercase text-muted-foreground">
                              <Building2 size={11} strokeWidth={1.5} /> My college
                            </div>
                            <select
                              data-testid="college-filter"
                              value={collegeFilter}
                              title="Highlights your college's buildings — your class buildings always stay visible"
                              onChange={(e) => { setCollegeFilter(e.target.value); localStorage.setItem("routerev_college", e.target.value); }}
                              className="w-full text-xs font-semibold rounded px-2 py-1.5 bg-white border border-border focus:border-brand outline-none"
                            >
                              <option value="">All colleges</option>
                              {campus.colleges.map((c) => (
                                <option key={c} value={c}>{c}</option>
                              ))}
                            </select>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>
              {effectiveLoc && (
                <button
                  data-testid="recenter-btn"
                  onClick={() => setFocus([...effectiveLoc])}
                  className="absolute bottom-3 right-3 z-[500] backdrop-blur-xl bg-white/85 border border-white/50 rounded-lg p-3 shadow-sm hover:bg-white transition-colors"
                  title={origin ? "Center on start point" : "Center on me"}
                >
                  <Navigation size={16} strokeWidth={1.5} className="text-brand" />
                </button>
              )}
            </div>

            {/* Travel legs */}
            {plan && plan.legs.length > 0 && (
              <div className="mt-4 bg-white border border-border rounded-xl p-5">
                <div className="flex items-center gap-1.5 mono text-[11px] tracking-[0.2em] uppercase text-brand mb-1">
                  <RouteIcon size={12} strokeWidth={1.5} /> Getting to class
                </div>
                <div className="divide-y divide-border">
                  {plan.legs.map((leg, i) => (
                    <div key={i}
                      data-testid={`leg-row-${i}`}
                      onMouseEnter={() => { setHighlight(i); if (legEndpoints[i]) setFocus([...legEndpoints[i].to]); }}
                      onMouseLeave={() => setHighlight(null)}
                      className={`flex items-center gap-2 rounded-lg transition-colors ${highlight === i ? "bg-brand/[0.06]" : ""}`}>
                      <div className="flex-1 min-w-0">
                        <Leg leg={leg} onDirections={directionsOn ? () => setDirIndex(i) : undefined} />
                      </div>
                      <button
                        data-testid={`leg-locate-${i}`}
                        onClick={() => { setHighlight(i); if (legEndpoints[i]) setFocus([...legEndpoints[i].to]); }}
                        title={`Show ${leg.to} on the map`}
                        className="shrink-0 p-2 rounded-md text-muted-foreground hover:text-brand hover:bg-brand/10 transition-colors">
                        <Crosshair size={15} strokeWidth={1.8} />
                      </button>
                      {leg.booking_url && (
                        <a href={leg.booking_url} target="_blank" rel="noreferrer"
                          data-testid={`leg-book-${i}`} title={`Book appointment — ${leg.to}`}
                          className="shrink-0 flex items-center gap-1 px-2 py-1.5 rounded-md text-[11px] font-semibold text-[#0891b2] bg-[#0891b2]/10 hover:bg-[#0891b2]/20 transition-colors">
                          <CalendarCheck size={14} strokeWidth={1.9} /> Book
                        </a>
                      )}
                      {leg.to_building_id && leg.kind !== "event" && (
                        <ClassCheckinButton
                          buildingId={leg.to_building_id}
                          name={leg.to}
                          active={activeCheckin}
                          onStarted={(doc) => setActiveCheckin(doc)}
                          onEnded={() => setActiveCheckin(null)}
                        />
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Schedule sidebar */}
          <div className="col-span-12 lg:col-span-4 order-2">
            {campus && (
              <ScheduleBuilder buildings={campus.buildings} classes={classes} reload={loadClasses} semester={semester} onSemester={setSemester} />
            )}
          </div>
        </div>

        {/* Gaps */}
        <div className="mt-6">
          <div className="flex items-baseline justify-between mb-3">
            <h2 className="font-display text-2xl font-semibold tracking-tight">Fill your gaps</h2>
            <span className="mono text-xs text-muted-foreground">{plan?.gaps?.length || 0} on {day}</span>
          </div>
          <NearYouRow />
          {plan && plan.gaps.length === 0 && (
            <div className="bg-white border border-border rounded-xl p-8 text-center text-sm text-muted-foreground">
              No gaps on {day}. Add classes with time between them to see lunch, study &amp; gym suggestions.
            </div>
          )}
          <div className="space-y-4">
            {plan?.gaps.map((g, i) => (
              <GapCard key={i} gap={g} index={i} onFocus={setFocus} />
            ))}
          </div>
        </div>
      </div>

      <DirectionsModal
        open={dirIndex !== null}
        onClose={() => setDirIndex(null)}
        leg={dirIndex !== null ? plan?.legs[dirIndex] : null}
        from={dirIndex !== null ? legEndpoints[dirIndex]?.from : null}
        to={dirIndex !== null ? legEndpoints[dirIndex]?.to : null}
        accessible={accessible}
      />
    </AppShell>
  );
}
